import { ENDPOINTS } from "@/constants/api/endpoints";
import { useApiQuery } from "../../hooks/use-api-query";
import { queryKeys } from "../../query-keys";
import {
  AmenityResponse,
  Amenity,
  FlattenedAmenity,
} from "@/types/references/amenity";
import { useMemo } from "react";

// Helper function to flatten amenities and their subtypes
const flattenAmenities = (amenities: Amenity[]): FlattenedAmenity[] => {
  const flattenedAmenities: FlattenedAmenity[] = [];

  amenities.forEach((amenity) => {
    // Add parent amenity if it should be in filter
    if (amenity.inFilter) {
      flattenedAmenities.push({
        id: amenity.slug,
        title: amenity.title,
        slug: amenity.slug,
        isSubType: false,
      });
    }

    // Add subtypes that should be in filter
    if (amenity.subType && amenity.subType.length > 0) {
      amenity.subType.forEach((subType) => {
        if (subType.inFilter) {
          flattenedAmenities.push({
            id: subType.slug,
            title: subType.title,
            slug: subType.slug,
            isSubType: true,
            parentTitle: amenity.title,
          });
        }
      });
    }
  });

  return flattenedAmenities;
};

// Base hook that fetches raw amenity data
const useRawAmenities = () => {
  return useApiQuery<AmenityResponse>(
    queryKeys.references.amenities,
    ENDPOINTS.REFERENCES.TAGS,
    {},
    {
      staleTime: 30 * 60 * 1000, // 30 minutes - amenities don't change frequently
      gcTime: 60 * 60 * 1000, // 1 hour
      refetchOnMount: false, // Use prefetched data, don't refetch on mount
      refetchOnWindowFocus: false, // Don't refetch on window focus for reference data
      // Keep every filterable tag, not just AMENITY — THEME tags
      // ("ساحلی", ...) are valid `amenity` filter values too.
      select: (data) => ({
        ...data,
        data: data.data.filter(
          (tag) => tag.inFilter || tag.subType?.some((sub) => sub.inFilter)
        ),
      }),
    }
  );
};

// Main hook that returns flattened amenities
export const useAmenities = () => {
  const { data: rawData, isLoading, error, ...rest } = useRawAmenities();

  const flattenedData = useMemo(() => {
    if (!rawData?.data) return null;

    return {
      ...rawData,
      data: flattenAmenities(rawData.data),
    };
  }, [rawData]);

  return {
    data: flattenedData,
    isLoading,
    error,
    ...rest,
  };
};
